// Purpose: forensics content viewer. Takes an ephemeral grant token in
// the URL query, fetches the target's decrypted content, and renders
// UTF-8 text inline with no copy/download affordances. Non-UTF-8 shows a
// SHA-256 banner instead of bytes. Closing the tab fires the close event.
//
// Phase 7.5 port: PageHeader with Admin > Forensics > Report breadcrumb
// + Card-wrapped content banner / pre block.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { closeForensicsAccess } from '../../api/admin-forensics.ts';
import { api } from '../../api/client.ts';
import { Breadcrumb, PageHeader } from '../../components/nav/PageHeader.tsx';
import { Button, Card } from '../../components/ui/index.ts';

interface ForensicsContent {
  accessId: string;
  mimeType: string;
  sizeBytes: number;
  contentBase64: string;
  sha256: string;
  expiresAt: string;
}

function base64ToBytes(b64: string): Uint8Array {
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function tryDecodeUtf8(bytes: Uint8Array): string | null {
  try {
    const dec = new TextDecoder('utf-8', { fatal: true });
    return dec.decode(bytes);
  } catch {
    return null;
  }
}

// Commit 8b.12.2: friendly copy keyed by the structured reason code
// from the 410 response. Falls back to a generic message so a future
// reason added server-side still renders something useful.
function unavailableReasonCopy(reason: string): string {
  switch (reason) {
    case 'share_consumed':
      return 'The recipient has already downloaded this share. Per AST retention policy, the encrypted blob is deleted on consume — the bytes are no longer recoverable.';
    case 'share_expired':
      return 'This share has expired. Its TTL elapsed and the encrypted blob was deleted by the cleanup sweep.';
    case 'share_revoked':
      return 'This share was revoked by its owner. The encrypted blob was deleted at revoke time.';
    case 'content_purged':
      return 'The encrypted blob is no longer present in storage. This usually means the content was deleted by the share lifecycle (consume / expire / revoke) or by an explicit purge.';
    default:
      return 'Content is no longer available.';
  }
}

export function ForensicsView() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const grant = params.get('grant');

  const [content, setContent] = useState<ForensicsContent | null>(null);
  const [decoded, setDecoded] = useState<string | null>(null);
  const [isBinary, setIsBinary] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Commit 8b.12.2: distinct state for the "grant succeeded but the
  // bytes are gone" 410 response. Renders a structured info card with
  // the reason from the server, no retry, no error tone — this is an
  // expected outcome of break-glass on consumed/expired content, not a
  // failure mode the operator should panic over.
  const [unavailable, setUnavailable] = useState<{ reason: string; message: string } | null>(null);
  const [startedAt] = useState<Date>(new Date());

  useEffect(() => {
    if (!grant) {
      setError('Missing grant token.');
      return;
    }
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const res = await api.get<ForensicsContent>('/admin/forensics/content', {
          params: { grant },
        });
        if (cancelled) return;
        setContent(res.data);
        const bytes = base64ToBytes(res.data.contentBase64);
        const utf8 = tryDecodeUtf8(bytes);
        if (utf8 === null) {
          setIsBinary(true);
        } else {
          setDecoded(utf8);
        }
      } catch (err) {
        if (cancelled) return;
        // Recognize the structured 410 from the backend and show the
        // friendly message instead of a generic error. axios surfaces
        // the response body via err.response.data.
        if (axios.isAxiosError(err) && err.response?.status === 410) {
          const data = err.response.data as { reason?: string; message?: string } | undefined;
          setUnavailable({
            reason: data?.reason ?? 'content_unavailable',
            message: data?.message ?? 'Content is no longer available.',
          });
          return;
        }
        setError((err as Error).message);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [grant]);

  // Fire close-event on unmount — covers tab close (beforeunload is best-
  // effort) and navigation away. Server-side accessId expiry still caps
  // the session even if the close call never lands.
  useEffect(() => {
    return () => {
      if (content) {
        const duration = Math.floor((Date.now() - startedAt.getTime()) / 1000);
        void closeForensicsAccess(content.accessId, content.sizeBytes, duration).catch(() => {});
      }
    };
  }, [content, startedAt]);

  if (!grant) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-body-m text-status-danger">Missing grant token.</p>
        <div>
          <Button variant="secondary" size="sm" onClick={() => nav('/admin/forensics')}>
            Back to forensics
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        breadcrumb={
          <Breadcrumb
            items={[
              { label: 'Admin', to: '/admin' },
              { label: 'Forensics', to: '/admin/forensics' },
              { label: 'Report' },
            ]}
          />
        }
        title="Forensics content view"
        subtitle={
          content
            ? `Session expires ${new Date(content.expiresAt).toLocaleTimeString()}`
            : undefined
        }
      />

      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      {unavailable ? (
        <Card>
          <div className="flex flex-col gap-2">
            <p className="text-body-m text-fg-primary">
              <strong>Content no longer available.</strong>
            </p>
            <p className="text-body-s text-fg-secondary">
              {unavailableReasonCopy(unavailable.reason)}
            </p>
            <p className="text-caption text-fg-muted">
              The grant + this attempt are recorded in the audit chain
              (admin.forensics.access_granted + admin.forensics.content_unavailable). Audit metadata
              for the original transfer (creator, recipient, filename, sizes, hashes) is preserved
              on the row in the audit log.
            </p>
            <div className="pt-2">
              <Button variant="secondary" size="sm" onClick={() => nav('/admin/forensics')}>
                Back to forensics
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {content && isBinary ? (
        <Card>
          <p className="text-body-m text-fg-primary">
            Binary content {content.sizeBytes.toLocaleString()} bytes (SHA-256:{' '}
            <code className="text-body-s text-fg-secondary">{content.sha256}</code>) — binary
            content cannot be displayed inline.
          </p>
        </Card>
      ) : null}

      {content && decoded !== null ? (
        <Card padding="none">
          <pre
            className="max-h-[70vh] overflow-auto p-4 text-body-s text-fg-primary"
            // Copy/download deliberately omitted per §6 break-glass policy.
            onCopy={(e) => e.preventDefault()}
          >
            {decoded}
          </pre>
        </Card>
      ) : null}

      {/* Commit 8b.12.3: include `unavailable` in the gate so the
          loader stops rendering once 410 arrives. Without this the
          "Streaming content…" placeholder shows alongside the
          unavailable Card, looking like the fetch is still in-flight. */}
      {!content && !error && !unavailable ? (
        <p className="text-body-m text-fg-muted">Streaming content…</p>
      ) : null}
    </div>
  );
}
